import React from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ExternalLink, MapPin, Clock, Ticket, Sparkles, Hammer, User, Calendar } from "lucide-react";

/**
 * Larger, encyclopaedic heritage detail modal.
 * Accepts a full heritage_site object (long_description, architecture, built_by,
 * visiting_hours, entry_fee, best_time, notable_features, lat, lng, etc.).
 */
export default function HeritageSiteModal({ site, open, onClose, accent = "#C05640", districtName = "" }) {
    if (!site) return null;
    const gMaps =
        site.google_maps_url ||
        (site.lat && site.lng
            ? `https://www.google.com/maps/search/?api=1&query=${site.lat},${site.lng}`
            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(site.name + " " + districtName)}`);

    const meta = [
        site.built_by && { icon: User, label: "Built By", value: site.built_by },
        site.architecture && { icon: Hammer, label: "Architecture", value: site.architecture },
        site.materials && { icon: Sparkles, label: "Materials", value: site.materials },
        site.visiting_hours && { icon: Clock, label: "Visiting Hours", value: site.visiting_hours },
        site.entry_fee && { icon: Ticket, label: "Entry", value: site.entry_fee },
        site.best_time && { icon: Calendar, label: "Best Time", value: site.best_time },
    ].filter(Boolean);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="bg-white w-[96vw] sm:max-w-5xl max-h-[92vh] overflow-y-auto p-0"
                data-testid="heritage-detail-modal"
            >
                {site.image && (
                    <div className="aspect-[21/9] overflow-hidden bg-ivory-deep relative">
                        <DialogTitle className="sr-only">{site.name}</DialogTitle>
                        <img src={site.image} alt={site.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
                        <div className="absolute bottom-5 left-6 right-6 text-white">
                            <div className="font-data text-[10px] tracking-[0.24em] uppercase opacity-90">
                                {site.type} · {site.era} {districtName && `· ${districtName}`}
                            </div>
                            <div className="font-display text-4xl md:text-5xl mt-2 leading-[1.05]">
                                {site.name}
                            </div>
                        </div>
                    </div>
                )}

                <div className="p-6 md:p-10">
                    {!site.image && (
                        <DialogHeader className="mb-4">
                            <div className="font-data text-[10px] tracking-[0.24em] uppercase" style={{ color: accent }}>
                                {site.type} · {site.era}
                            </div>
                            <DialogTitle className="font-display text-4xl md:text-5xl">{site.name}</DialogTitle>
                        </DialogHeader>
                    )}
                    <DialogDescription className="font-body italic text-base ink-mute">
                        {site.condition && <span>Status: {site.condition}</span>}
                    </DialogDescription>

                    <div className="editorial-rule my-6" />

                    <div className="grid md:grid-cols-12 gap-8">
                        <div className="md:col-span-8 space-y-5">
                            <div>
                                <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-2">The Site</div>
                                <p className="font-body text-base md:text-lg leading-relaxed">
                                    {site.long_description || site.significance}
                                </p>
                            </div>

                            {site.long_description && site.significance && (
                                <div>
                                    <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-2">Significance</div>
                                    <p className="font-body text-base leading-relaxed">{site.significance}</p>
                                </div>
                            )}

                            {site.notable_features?.length > 0 && (
                                <div>
                                    <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-3">Notable Features</div>
                                    <ul className="space-y-2.5">
                                        {site.notable_features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-3 font-body text-sm md:text-base">
                                                <span
                                                    className="w-1.5 h-1.5 rounded-full mt-2.5 shrink-0"
                                                    style={{ background: accent }}
                                                />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <aside className="md:col-span-4">
                            <div className="border hairline rounded-md p-5 sticky top-4 bg-ivory">
                                <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-4">At a Glance</div>
                                <div className="space-y-4">
                                    {meta.map((m) => {
                                        const Icon = m.icon;
                                        return (
                                            <div key={m.label}>
                                                <div className="flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.16em] ink-mute">
                                                    <Icon className="w-3 h-3" /> {m.label}
                                                </div>
                                                <div className="font-body text-sm mt-0.5 leading-snug">{m.value}</div>
                                            </div>
                                        );
                                    })}
                                    {site.lat && site.lng && (
                                        <div>
                                            <div className="flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.16em] ink-mute">
                                                <MapPin className="w-3 h-3" /> Coordinates
                                            </div>
                                            <div className="font-data text-sm mt-0.5">
                                                {site.lat.toFixed(4)}, {site.lng.toFixed(4)}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <a
                                    href={gMaps}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="heritage-modal-google-maps"
                                    className="mt-6 inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-ink text-ivory font-data text-[11px] tracking-[0.18em] uppercase rounded-sm hover:opacity-90 transition-opacity"
                                >
                                    Open in Google Maps <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </div>
                        </aside>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
